import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Users } from 'lucide-react';
import { useUserAdministration } from '@/hooks/useUserAdministration';
import { UserList } from '@/components/admin/UserList';
import { UserDetail } from '@/components/admin/UserDetail';

/**
 * User Administration Page
 * 
 * Two-panel layout for managing users:
 * - Left panel: Searchable list of all users
 * - Right panel: Detailed view of selected user with linked content
 * 
 * Implements User Story 1: Search and View User Details
 */
export default function UserAdministrationPage() {
  const { t } = useTranslation('auth');
  const { userId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();
  
  const {
    users,
    selectedUser,
    linkedContent,
    loadingUsers,
    loadingDetails,
    error,
    selectUser,
    clearSelectedUser,
    banAccount,
    unbanAccount,
    clearError,
  } = useUserAdministration();

  // Load user when URL has userId
  useEffect(() => {
    if (userId) {
      selectUser(userId);
    } else {
      clearSelectedUser();
    }
  }, [userId, selectUser, clearSelectedUser]);

  // Navigate to user detail
  const handleSelectUser = (id: string) => {
    navigate(`/admin/users/${id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          {selectedUser ? (
            <button
              onClick={() => navigate('/admin/users')}
              className="inline-flex items-center gap-2 text-neutral-600 hover:text-primary-600 transition-colors mb-4"
            >
              <ArrowLeft size={20} />
              <span>Back to list</span>
            </button>
          ) : (
            <Link
              to="/admin/dashboard"
              className="inline-flex items-center gap-2 text-neutral-600 hover:text-primary-600 transition-colors mb-4"
            >
              <ArrowLeft size={20} />
              <span>Back to Dashboard</span>
            </Link>
          )}
          
          <div className="flex items-center gap-3">
            <Users className="text-primary-600" size={32} />
            <h1 className="text-3xl font-bold text-neutral-900">
              {t('userAdmin.title')}
            </h1>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-800 font-medium text-sm"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Two-Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel: User List */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-neutral-900 mb-4">
              All Users ({users.length})
            </h2>
            <UserList
              users={users}
              selectedUserId={selectedUser?.id || null}
              onSelectUser={handleSelectUser}
              loading={loadingUsers}
            />
          </div>

          {/* Right Panel: User Details */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            {loadingDetails ? (
              <div className="space-y-6">
                <div className="h-8 bg-neutral-200 animate-pulse rounded w-1/3" />
                <div className="space-y-4">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="h-16 bg-neutral-200 animate-pulse rounded-lg" />
                  ))}
                </div>
              </div>
            ) : selectedUser ? (
              <UserDetail
                user={selectedUser}
                linkedContent={linkedContent}
                loading={false}
                onBan={banAccount}
                onUnban={unbanAccount}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-neutral-500">
                <Users className="mb-4 text-neutral-400" size={64} />
                <p className="text-lg font-medium">{t('userAdmin.selectUser')}</p>
                <p className="text-sm mt-2 text-center max-w-md">
                  Select a user from the list to view their complete profile information and linked content.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
